import { getRole } from '@/api/system/role';
import { getDeptTree } from '@/api/system/dept';
import { deleteUser, getUser, getUserDetail, getUserGetinfo, postUser, putUser, putPwdReset, putStatus } from '@/api/system/user';
import { addWorkOrder, completeWorkOrder, assignWorkOrder, rechargeBalance } from '@/api/workorder';
import CommonLayout, { CommonLayoutInstance } from '@/components/CommonLayout';
import { Form, Input, Space, Select, Radio, TreeSelect, Modal, InputNumber, message } from 'antd';
import React, { Fragment, useEffect, useRef, useState, useMemo } from 'react';
import dayjs from 'dayjs';
import { CloseCircleFilled, CheckCircleFilled } from '@ant-design/icons';

/** 服务商角色标识，用于判断是否需要限制部门和用户可见范围 */
const PROVIDER_ROLE_KEY = 'provider';

const User = () => {
  const CommonLayoutRef = useRef<CommonLayoutInstance<{ params: {}; model: {}; drawerParams: {}; drawerModel: {} }>>(null);
  const [roleTree, setRoleTree] = useState<{ id?: string; label?: string; children?: { id?: string; label?: string }[] }[]>([]);
  const [deptTree, setDeptTree] = useState<{ id?: string; label?: string; children?: { id?: string; label?: string }[] }[]>([]);
  const [currentRoleKey, setCurrentRoleKey] = useState<string>('');
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [userInfoLoaded, setUserInfoLoaded] = useState(false);

  // 充值弹窗状态
  const [rechargeModalVisible, setRechargeModalVisible] = useState(false);
  const [rechargeUser, setRechargeUser] = useState<{ id: string; username: string; nickName: string } | null>(null);
  const [rechargeAmount, setRechargeAmount] = useState<number>(0);
  const [rechargeLoading, setRechargeLoading] = useState(false);

  /** 当前用户是否为管理员角色（可进行充值和扣款操作） */
  const isAdmin = useMemo(() => {
    return currentRoleKey === 'admin' || currentRoleKey === 'super_admin';
  }, [currentRoleKey]);

  /** 当前用户是否为管理员或服务商角色（可进行充值操作，服务商只能充值不能扣款） */
  const canRecharge = useMemo(() => {
    return isAdmin || currentRoleKey === PROVIDER_ROLE_KEY;
  }, [currentRoleKey, isAdmin]);

  /** 打开充值弹窗 */
  const handleOpenRecharge = (record: { id: string; username: string; nickName: string }) => {
    setRechargeUser(record);
    setRechargeAmount(0);
    setRechargeModalVisible(true);
  };

  /** 确认充值/扣款 */
  const handleConfirmRecharge = async () => {
    if (!rechargeUser || rechargeAmount === 0) {
      message.warning('请输入有效的金额（正数为充值，负数为扣款）');
      return;
    }
    setRechargeLoading(true);
    try {
      const isDeduction = rechargeAmount < 0;
      const actionLabel = isDeduction ? '扣款' : '充值';

      // 1. 更新客户余额
      await rechargeBalance({
        customerId: rechargeUser.id,
        amount: rechargeAmount,
      });

      // 2. 创建工单
      const res = await addWorkOrder({
        orderType: isDeduction ? '扣款' : '充值',
        customerId: rechargeUser.id,
        description: `客户 ${rechargeUser.nickName || rechargeUser.username} ${actionLabel} ¥${Math.abs(rechargeAmount).toFixed(2)}`,
      });

      if (res.result?.id) {
        const workOrderId = res.result.id;

        // 从 localStorage 获取当前登录用户ID
        let operatorId = currentUserId;
        if (!operatorId) {
          try {
            const storedUser = JSON.parse(localStorage.getItem('$UserInfo$') || '{}');
            operatorId = storedUser?.id || '';
          } catch {}
        }

        // 3. 分配给当前操作人
        if (operatorId) {
          await assignWorkOrder({
            id: workOrderId,
            providerId: operatorId,
          });
        }

        // 4. 直接完成工单
        await completeWorkOrder({
          id: workOrderId,
          remark: `${actionLabel}金额：¥${Math.abs(rechargeAmount).toFixed(2)}`,
        });
      }

      message.success(`${actionLabel}成功`);
      setRechargeModalVisible(false);
      CommonLayoutRef.current?.reload();
    } catch (error: any) {
      console.error('充值失败:', error);
      message.error(error?.message || '充值失败，请重试');
    } finally {
      setRechargeLoading(false);
    }
  };

  /** 当前用户是否为服务商角色 */
  const isProvider = useMemo(() => {
    return currentRoleKey === PROVIDER_ROLE_KEY;
  }, [currentRoleKey]);

  /** 获取当前用户角色信息（直接调API，不依赖localStorage缓存） */
  const fetchCurrentUserInfo = async () => {
    try {
      const res = await getUserGetinfo();
      if (res.result?.roleKey) {
        setCurrentRoleKey(res.result.roleKey);
      }
      if (res.result?.userId || res.result?.id) {
        setCurrentUserId(res.result.userId || res.result.id);
      }
    } catch {}
    setUserInfoLoaded(true);
  };

  /** 服务商可见的部门树（仅客户部门） */
  const filteredDeptTree = useMemo(() => {
    if (!isProvider) return deptTree;
    // 只保留"客户"部门节点
    const filterCustomerDept = (nodes: typeof deptTree): typeof deptTree => {
      return nodes
        .map((node) => {
          if (node.label === '客户') {
            return node;
          }
          if (node.children?.length) {
            const filteredChildren = filterCustomerDept(node.children);
            if (filteredChildren.length > 0) {
              return { ...node, children: filteredChildren };
            }
          }
          return null;
        })
        .filter(Boolean) as typeof deptTree;
    };
    return filterCustomerDept(deptTree);
  }, [isProvider, deptTree]);

  /** 获取客户部门ID，用于默认过滤用户列表 */
  const customerDeptId = useMemo(() => {
    if (!isProvider || !filteredDeptTree.length) return undefined;
    // 找到客户部门的ID
    const findCustomerId = (nodes: typeof deptTree): string | undefined => {
      for (const node of nodes) {
        if (node.label === '客户' && node.id) return node.id;
        if (node.children?.length) {
          const found = findCustomerId(node.children);
          if (found) return found;
        }
      }
      return undefined;
    };
    return findCustomerId(filteredDeptTree);
  }, [isProvider, filteredDeptTree]);

  const getDeptTreeList = async () => {
    try {
      const res = await getDeptTree({ status: '1' });
      setDeptTree(res.result || []);
    } catch {}
  };
  const getRoleList = async () => {
    try {
      const res = await getRole({ isAll: true, status: '1' });
      setRoleTree(res.result?.list || []);
    } catch {}
  };
  useEffect(() => {
    getRoleList();
    getDeptTreeList();
    fetchCurrentUserInfo();
  }, []);

  if (!userInfoLoaded) return null;

  return (
    <>
    <CommonLayout
      key={currentRoleKey} // 角色变化时强制重新挂载，确保权限过滤生效
      ref={CommonLayoutRef}
      queryPermission="admin:sysUser:query"
      breadcrumb={{
        title: '用户管理',
        describe: '用户管理页面提供数据筛选、新增用户、重置密码、用户数据列表等功能模块。',
      }}
      columns={[
        {
          dataIndex: 'username',
          title: '用户名',
        },
        {
          dataIndex: 'nickName',
          title: '昵称',
        },
        {
          dataIndex: 'phone',
          title: '手机',
        },
        {
          dataIndex: 'dept',
          title: '部门',
          render: (dept) => dept?.dept_name,
        },
        {
          dataIndex: 'status',
          title: '状态',
          // render: (v: number) => ({ 1: '禁用', 2: '正常' })[v],
          // render: (v: number) => <Switch checkedChildren="正常" unCheckedChildren="禁用" checked={v === 2} />,
          render: (value: string) => {
            if (value === '1')
              return (
                <Space>
                  <CloseCircleFilled style={{ color: '#ef8e62' }} />
                  禁用
                </Space>
              );
            if (value === '2')
              return (
                <Space>
                  <CheckCircleFilled style={{ color: '#52c41a' }} />
                  正常
                </Space>
              );
            return '-';
          },
        },
        {
          dataIndex: 'balance',
          title: '余额',
          width: 160,
          render: (value: number, record: any) => {
            // 仅客户角色显示余额
            const isCustomer = record.dept?.dept_name === '客户' || record.roleKey === 'customer';
            if (!isCustomer) return <span style={{ color: '#999' }}>-</span>;
            return (
              <Space>
                <span style={{ color: value > 0 ? '#52c41a' : '#999', fontWeight: 500 }}>
                  ¥{(value || 0).toFixed(2)}
                </span>
                {canRecharge && (
                  <a
                    style={{ color: '#1890ff', cursor: 'pointer', fontSize: '13px' }}
                    onClick={(e) => { e.stopPropagation(); handleOpenRecharge(record); }}
                  >
                    充值
                  </a>
                )}
              </Space>
            );
          },
        },
        {
          dataIndex: 'created',
          title: '创建时间',
          render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
        },
      ]}
      ajaxJson={{
        get: {
          // @ts-ignore
          ajax: (params: any) => {
            // 服务商角色只能查看客户部门的用户
            if (isProvider && customerDeptId) {
              return getUser({ ...params, deptId: customerDeptId });
            }
            return getUser(params);
          },
          isNotToSetValues: true,
        },
        // @ts-ignore
        delete: { ajax: deleteUser },
        putStatus2: {
          // @ts-ignore
          ajax: putStatus,
          convertValue: (data) => ({ ...data, status: '2' }),
        },
        putStatus1: {
          // @ts-ignore
          ajax: putStatus,
          convertValue: (data) => ({ ...data, status: '1' }),
        },
      }}
      // tableProps={{ pagination: false }}
      commonCbk={(key) => {
        if (key !== 'get') {
          CommonLayoutRef.current?.reload();
        }
      }}
      searchFormChildren={
        <Space>
          <Form.Item name="username">
            <Input placeholder="名称" allowClear key="search-username"></Input>
          </Form.Item>
          <Form.Item name="phone">
            <Input placeholder="手机" allowClear key="search-phone"></Input>
          </Form.Item>
          <Form.Item name="status">
            <Select
              style={{ width: '120px' }}
              placeholder="状态"
              allowClear
              options={[
                { value: '2', label: '正常' },
                { value: '1', label: '禁用' },
              ]}
            ></Select>
          </Form.Item>
          <Form.Item name="deptId">
            <TreeSelect
              key="search-deptId"
              allowClear
              treeDefaultExpandAll
              style={{ width: '200px' }}
              placeholder="部门"
              treeData={filteredDeptTree}
              fieldNames={{ label: 'label', value: 'id', children: 'children' }}
            ></TreeSelect>
          </Form.Item>
          <Form.Item name="roleId">
            <Select
              style={{ width: '140px' }}
              placeholder="角色"
              allowClear
              options={roleTree}
              fieldNames={{ label: 'roleName', value: 'id' }}
            ></Select>
          </Form.Item>
        </Space>
      }
      drawerJson={{
        ajaxJson: {
          edit: {
            // @ts-ignore
            ajax: putUser,
          },
          add: {
            // @ts-ignore
            ajax: postUser,
            cbk: () => {},
          },

          get: {
            // @ts-ignore
            ajax: getUserDetail,
          },
          putPwdReset: {
            // @ts-ignore
            ajax: putPwdReset,
          },
        },
        commonCbk: (key) => {
          if (key !== 'get') {
            CommonLayoutRef.current?.reload();
          }
        },
        formProps: { layout: 'vertical' },

        formChildrenJson: {
          putPwdReset: (
            <Fragment key="putPwdReset">
              <Form.Item name="password" label="新密码">
                <Input.Password placeholder="请输入"></Input.Password>
              </Form.Item>
              <Form.Item name="userId" noStyle>
                <div></div>
              </Form.Item>
            </Fragment>
          ),
        },
        renderFormChildren: ({ ajaxKey, isDetail: disabled }) => (
          <Fragment key={ajaxKey || 'edit'}>
            <Form.Item name="username" label="用户名称" rules={[{ required: true }]}>
              <Input placeholder="请输入" disabled={disabled}></Input>
            </Form.Item>
            <Form.Item name="nickName" label="用户昵称" rules={[{ required: true }]}>
              <Input placeholder="请输入" disabled={disabled}></Input>
            </Form.Item>
            <Form.Item name="deptId" label="部门" rules={[{ required: true }]}>
              <TreeSelect placeholder="请选择" disabled={disabled} treeDefaultExpandAll treeData={filteredDeptTree} fieldNames={{ label: 'label', value: 'id', children: 'children' }}></TreeSelect>
            </Form.Item>
            <Form.Item name="phone" label="手机号" rules={[{ required: true }]}>
              <Input placeholder="请输入" disabled={disabled}></Input>
            </Form.Item>
            <Form.Item name="email" label="邮箱" rules={[{ required: true }]}>
              <Input placeholder="请输入" disabled={disabled}></Input>
            </Form.Item>
            <Form.Item dependencies={['id']} noStyle>
              {(form) => {
                const id = form.getFieldValue('id');
                if (!id)
                  return (
                    <Form.Item name="password" label="密码" rules={[{ required: true }]}>
                      <Input.Password placeholder="请输入" disabled={disabled}></Input.Password>
                    </Form.Item>
                  );
                return <></>;
              }}
            </Form.Item>

            <Form.Item name="sex" label="性别" rules={[{ required: true }]}>
              <Radio.Group
                disabled={disabled}
                options={[
                  { label: '男', value: '男' },
                  { label: '女', value: '女' },
                ]}
              ></Radio.Group>
            </Form.Item>
            <Form.Item name="roleId" label="角色" rules={[{ required: true }]}>
              <Select disabled={disabled} placeholder="请选择" options={roleTree} fieldNames={{ label: 'roleName', value: 'id' }}></Select>
            </Form.Item>
            <Form.Item name="status" label="状态" rules={[{ required: true }]}>
              <Radio.Group
                disabled={disabled}
                options={[
                  { value: '2', label: '正常' },
                  { value: '1', label: '禁用' },
                ]}
              ></Radio.Group>
            </Form.Item>
            <Form.Item name="remark" label="备注">
              <Input.TextArea placeholder="请输入" disabled={disabled}></Input.TextArea>
            </Form.Item>
            <Form.Item name="id" noStyle>
              <div></div>
            </Form.Item>
          </Fragment>
        ),
      }}
      operateButtonList={[
        {
          name: '编辑',
          ajaxKey: 'edit',
          props: { type: 'primary' },
          isToShowDrawer: true,
          sortData: ({ id }) => ({ id }),
          isToReload: true,
          permission: 'admin:sysUser:edit',
          sortItem: ({ username }) => (username === 'super_admin' ? { props: { type: 'primary', disabled: true } } : { props: { type: 'primary' } }),
        },
        {
          name: '重置密码',
          ajaxKey: 'putPwdReset',
          isToShowDrawer: true,
          sortData: ({ id }) => ({ userId: id }),
          permission: 'admin:sysUser:passwordSet',
          sortItem: ({ username }) => (username === 'super_admin' ? { props: { type: 'primary', disabled: true } } : { props: { type: 'primary' } }),
        },
        {
          name: '禁用',
          ajaxKey: 'putStatus1',
          isToPopConfirm: true,
          isToAjax: true,
          sortData: ({ id }) => ({ userId: id }),
          filter: ({ status }) => status === '1',
          popconfirmProps: { title: '是否确认禁用用该用户?' },
          permission: 'admin:sysUser:edit',
          sortItem: ({ username }) => (username === 'super_admin' ? { props: { type: 'primary', disabled: true } } : { props: { type: 'primary' } }),
        },
        {
          name: '启用',
          ajaxKey: 'putStatus2',
          isToPopConfirm: true,
          isToAjax: true,
          sortData: ({ id }) => ({ userId: id }),
          filter: ({ status }) => status === '2',
          popconfirmProps: { title: '是否确认启用该用户?' },
          permission: 'admin:sysUser:edit',
          sortItem: ({ username }) => (username === 'super_admin' ? { props: { type: 'primary', disabled: true } } : { props: { type: 'primary' } }),
        },
        {
          name: '删除',
          ajaxKey: 'delete',
          props: { type: 'primary', danger: true },
          isToPopConfirm: true,
          isToAjax: true,
          popconfirmProps: { title: '是否确认删除该用户?' },
          sortData: ({ id }) => ({ id }),
          permission: 'admin:sysUser:remove',
          sortItem: ({ username }) => (username === 'super_admin' ? { props: { type: 'primary', disabled: true } } : { props: { type: 'primary' } }),
        },
      ]}
      leftOperateButtonList={[
        {
          name: '新增',
          permission: 'admin:sysUser:add',
          ajaxKey: 'add',
          props: { type: 'primary' },
          isToShowDrawer: true,
          drawerProps: { title: '新增' },
          sortData: () => ({ status: '2', sex: '男' }),
        },
      ]}
    ></CommonLayout>

    {/* 充值/扣款弹窗 */}
    <Modal
      title={`客户${isAdmin ? '充值/扣款' : '充值'} - ${rechargeUser?.nickName || rechargeUser?.username || ''}`}
      open={rechargeModalVisible}
      onOk={handleConfirmRecharge}
      onCancel={() => setRechargeModalVisible(false)}
      confirmLoading={rechargeLoading}
      okText="确认"
      cancelText="取消"
      width={420}
    >
      <div style={{ padding: '20px 0' }}>
        <Form layout="vertical">
          <Form.Item label="客户信息">
            <Input value={rechargeUser?.nickName || rechargeUser?.username} disabled />
          </Form.Item>
          <Form.Item label={`${isAdmin ? '金额（元）' : '充值金额（元）'}`} required>
            <InputNumber
              style={{ width: '100%' }}
              min={isAdmin ? undefined : 0}
              precision={2}
              step={10}
              placeholder={isAdmin ? "正数充值，负数扣款" : "请输入充值金额"}
              value={rechargeAmount}
              onChange={(val) => setRechargeAmount(val || 0)}
              prefix="¥"
            />
          </Form.Item>
          <Form.Item label="说明">
            <div style={{ color: '#666', fontSize: '13px' }}>
              {isAdmin
                ? '输入正数为充值，输入负数为扣款。操作后将创建对应的工单记录。'
                : '充值后将创建对应的工单记录，金额将计入客户余额。'}
            </div>
          </Form.Item>
        </Form>
      </div>
    </Modal>
    </>
  );
};
export default User;
